import {
  FieldControl,
  FieldDescription,
  FieldError,
  FieldLabel,
  FieldLabelRow,
  Field as FieldRoot,
} from '@savvy-kit/components/field';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@savvy-kit/components/input-group';
import { Tooltip, TooltipContent, TooltipTrigger } from '@savvy-kit/components/tooltip';
import { cn } from '@savvy-kit/lib/utils';
import { InfoIcon } from 'lucide-react';

type InputFieldProps = Omit<React.ComponentProps<'input'>, 'size'> & {
  readonly id: string;
  readonly size?: 'sm' | 'default' | 'lg';
  readonly label?: string;
  readonly description?: string;
  readonly error?: boolean;
  readonly errorMessage?: string;
  readonly prependInputAddon?: React.ReactNode;
  readonly appendInputAddon?: React.ReactNode;
  readonly disabled?: boolean;
  readonly showInfoIcon?: boolean;
  readonly infoTooltipMessage?: string;
  // Sizing/layout for the field as a whole. `className` reaches the inner <input>,
  // so it cannot size the group — a flex-item parent needs this to stop the field
  // collapsing to its content width.
  readonly containerClassName?: string;
};

export function InputField({
  id,
  size,
  label,
  description,
  error,
  errorMessage,
  prependInputAddon,
  appendInputAddon,
  disabled,
  showInfoIcon,
  infoTooltipMessage,
  containerClassName,
  ...props
}: InputFieldProps) {
  const showInfoTooltip = showInfoIcon && infoTooltipMessage;
  const showRequired = props.required === true;

  return (
    <FieldRoot invalid={error} disabled={disabled} className={containerClassName}>
      {label && (
        <FieldLabelRow>
          <FieldLabel required={showRequired} className={cn(size === 'sm' && 'text-xs')}>
            {label}
          </FieldLabel>
          {showInfoTooltip && (
            <Tooltip>
              <TooltipTrigger render={<InfoIcon className="size-3" />} />
              <TooltipContent>
                <p>{infoTooltipMessage}</p>
              </TooltipContent>
            </Tooltip>
          )}
        </FieldLabelRow>
      )}

      <InputGroup size={size} data-disabled={disabled}>
        {/*
          The input is slotted through the shell so it receives the composed `aria-describedby`.
          The function form is used because `InputGroup` owns the box and the addons, so only the
          input itself can be the slot.
        */}
        <FieldControl id={id} required={props.required} disabled={disabled}>
          {(slot) => (
            <InputGroupInput
              {...props}
              aria-invalid={error}
              // A caller's own `aria-describedby` is merged, never dropped and never allowed to
              // replace the shell's: overriding it would sever the association the shell exists for.
              aria-describedby={
                [props['aria-describedby'], slot['aria-describedby']].filter(Boolean).join(' ') || undefined
              }
              // The caller's own `id`, not `slot.id`: base-ui hands back a generated id on the first
              // commit, so a consumer effect doing `document.getElementById(id)?.focus()` finds
              // nothing and the focus is lost. Both values agree once mounted.
              id={id}
              disabled={disabled}
            />
          )}
        </FieldControl>
        {prependInputAddon && <InputGroupAddon>{prependInputAddon}</InputGroupAddon>}
        {appendInputAddon && <InputGroupAddon align="inline-end">{appendInputAddon}</InputGroupAddon>}
      </InputGroup>

      {description && <FieldDescription>{description}</FieldDescription>}
      <FieldError error={error} errorMessage={errorMessage} />
    </FieldRoot>
  );
}

export default InputField;
